import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import {
  AlertTriangle,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  ImageOff,
  Inbox,
  ListFilter,
  LogIn,
  LogOut,
  MessageSquare,
  Repeat,
  RefreshCw,
  ScanFace,
  Search,
  Sparkle,
  Trash2,
  UserPlus,
  Users,
  UserX,
  X,
  XCircle,
} from 'lucide-react';

import './SelfieExceptionsPage.css';
import config from '../../configLoader';
import { useSiteContext } from '../../SiteContext';
import { useBlobImageUrl } from '../../hooks/useBlobImageUrl';
import usePayPeriods from '../../hooks/usePayPeriods';
import { useSelectPayPeriod } from '../../hooks/useSelectPayPeriod';
import WeekPickerNav from '../../components/WeekPickerNav';
import EnrollmentPhotoModal, {
  EnrollmentChangeAction,
  EnrollmentModalEmployee,
} from '../../components/enrollment/EnrollmentPhotoModal';
import { EnmSpin } from '../../components/enrollment/EnrollmentChip';
import {
  getEnrollmentIssues,
  getEnrollmentPhotoErrorMessage,
  getEnrollmentRetryConflict,
  retryEnrollment,
} from '../../api/enrollmentPhoto';
import { consumeStashedDeepLink } from '../../interfaces/DeepLinkInterface';
import {
  getReviewConflict,
  getSelfieVerificationExceptions,
  getSelfieVerificationExceptionsErrorMessage,
  reviewSelfieException,
  reviewSelfieExceptionErrorMessage,
} from '../../api/selfieVerificationExceptions';
import type {
  EmployeeEnrollment,
  EnrollmentIssue,
  EnrollmentMatchedEmployee,
  SelfieExceptionReason,
  SelfieVerificationExceptionEmployee,
  SelfieVerificationExceptionItem,
  SelfieVerificationExceptionsResponse,
} from '../../interfaces/SelfieEnrollment';
import SelfieExceptionDetailModal from './SelfieExceptionDetailModal';
import SelfieEnrollmentView, { countEnrollmentIssues } from './SelfieEnrollmentView';

/* ═══════════════════════════════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════════════════════════════ */

export type StatusFilter = 'pending' | 'reviewed' | 'all';
export type SortKey = 'punch_desc' | 'punch_asc' | 'conf_asc' | 'employee';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'punch_desc', label: 'Newest punch first' },
  { value: 'punch_asc', label: 'Oldest punch first' },
  { value: 'conf_asc', label: 'Lowest confidence first' },
  { value: 'employee', label: 'Employee A → Z' },
];

/* ═══════════════════════════════════════════════════════════════════════════
   Reason vocabulary — the single source for every human-facing string.
   Nothing renders a raw snake_case value anywhere (handoff §6d).
   ═══════════════════════════════════════════════════════════════════════════ */

export interface ReasonMeta {
  label: string;
  /** The "family" one-liner under the chip — teaches the taxonomy cheaply. */
  family: string;
  /** CSS modifier shared by the chip and the detail's verdict band. */
  cls: 'r-mismatch' | 'r-noface' | 'r-error' | 'r-enroll';
  /** Swatch for the reason filter menu. */
  dot: string;
  Icon: React.ComponentType<{ className?: string }>;
  /** Verdict sentence; `pct` is null when nothing was scored. */
  sentence: (pct: number | null, thresholdPct: number) => React.ReactNode;
  /** "What to do about it" line under the dashed rule. */
  action: React.ReactNode;
}

export const REASON_META: Record<SelfieExceptionReason, ReasonMeta> = {
  face_mismatch: {
    label: 'Face mismatch',
    family: 'Possible identity issue',
    cls: 'r-mismatch',
    dot: '#e0533f',
    Icon: UserX,
    sentence: (pct, thresholdPct) => (
      <>
        Azure found a face and compared it to the enrollment photo, but scored the match{' '}
        <b>below the {thresholdPct}% threshold</b>
        {pct != null ? (
          <>
            . At <b>{pct}%</b> this is <b>{bandLabel(pct / 100, thresholdPct / 100).toLowerCase()}</b>.
          </>
        ) : (
          '.'
        )}
      </>
    ),
    action: (
      <>
        <b>Look at both photos.</b> Same person? Mark reviewed with a one-line note. Not the same
        person? Escalate to the supervisor before this week is approved.
      </>
    ),
  },
  no_face_detected: {
    label: 'No face detected',
    family: 'Photo quality',
    cls: 'r-noface',
    dot: '#d4881a',
    Icon: Camera,
    sentence: () => (
      <>
        Azure could not find a face in the punch selfie, so <b>no comparison was scored</b>.
      </>
    ),
    action: (
      <>
        <b>Usually a dark, blurred or obstructed capture.</b> Check the selfie, then have the crew
        lead fix the conditions — lighting, phone case over the lens, hat brim.
      </>
    ),
  },
  verification_error: {
    label: 'Verification error',
    family: 'System issue',
    cls: 'r-error',
    dot: '#6b7686',
    Icon: AlertTriangle,
    sentence: () => (
      <>
        The verification job failed before any comparison could run.{' '}
        <b>Nothing here reflects on the employee.</b>
      </>
    ),
    action: (
      <>
        <b>No employee action.</b> If several land in the same window it is an Azure Face or
        background-job outage — check Sync Status, then mark these reviewed.
      </>
    ),
  },
  employee_not_enrolled: {
    label: 'Not enrolled',
    family: 'Onboarding gap',
    cls: 'r-enroll',
    dot: '#7a5cf0',
    Icon: UserPlus,
    sentence: () => (
      <>
        This employee has <b>no enrollment photo on file</b>, so there was nothing to compare the
        selfie against.
      </>
    ),
    action: (
      <>
        <b>Add an enrollment photo</b> on the employee record. Every punch keeps raising an exception
        until you do.
      </>
    ),
  },
};

const REASON_ORDER: SelfieExceptionReason[] = [
  'face_mismatch',
  'no_face_detected',
  'verification_error',
  'employee_not_enrolled',
];

/** Unknown reasons still get a readable chip rather than raw snake_case. */
export function reasonMeta(reason: string): ReasonMeta {
  const known = REASON_META[reason as SelfieExceptionReason];
  if (known) return known;
  return {
    ...REASON_META.verification_error,
    label: reason
      .split('_')
      .filter(Boolean)
      .map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(' '),
    family: 'Unrecognized reason',
  };
}

export const AZURE_STATUS_LABELS: Record<string, string> = {
  no_match: 'No match',
  no_face: 'No face',
  error: 'Error',
  failed: 'Failed',
};

/* ═══════════════════════════════════════════════════════════════════════════
   Pure helpers
   ═══════════════════════════════════════════════════════════════════════════ */

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Fri, Apr 24" in the viewer's local time. */
export function formatDayMono(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${DOW[d.getDay()]}, ${MON[d.getMonth()]} ${d.getDate()}`;
}

/** "6:05 PM" in the viewer's local time. */
export function formatTimeMono(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  let h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
}

export function formatDateTimeMono(iso: string): string {
  return `${formatDayMono(iso)}, ${formatTimeMono(iso)}`;
}

/** Date-only range, e.g. "Apr 20 – 26, 2026". Inputs are "YYYY-MM-DD". */
export function formatPeriodRange(startDate: string, endDate: string): string {
  const s = parseDateOnly(startDate);
  const e = parseDateOnly(endDate);
  if (!s || !e) return '—';
  const year = e.getFullYear();
  if (s.getMonth() === e.getMonth()) {
    return `${MON[s.getMonth()]} ${s.getDate()} – ${e.getDate()}, ${year}`;
  }
  return `${MON[s.getMonth()]} ${s.getDate()} – ${MON[e.getMonth()]} ${e.getDate()}, ${year}`;
}

/**
 * "YYYY-MM-DD" → a LOCAL midnight Date. `new Date("2026-04-20")` parses as UTC
 * and can land on the previous day west of Greenwich, which would silently shift
 * the new-hire window by a day.
 */
export function parseDateOnly(value: string | null): Date | null {
  if (!value) return null;
  const parts = value.slice(0, 10).split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map((p) => parseInt(p, 10));
  if ([y, m, d].some((n) => isNaN(n))) return null;
  return new Date(y, m - 1, d);
}

/** "6 min after the punch" / "1h 49m after the punch". */
export function formatGap(punchedAt: string, verifiedAt: string | null): string | null {
  if (!verifiedAt) return null;
  const from = new Date(punchedAt).getTime();
  const to = new Date(verifiedAt).getTime();
  if (isNaN(from) || isNaN(to)) return null;
  const mins = Math.round((to - from) / 60000);
  if (mins < 0) return 'before the punch';
  if (mins === 0) return 'same minute as the punch';
  if (mins < 60) return `${mins} min after the punch`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h after the punch` : `${h}h ${m}m after the punch`;
}

/**
 * Confidence band copy. The fourth band matters: Azure can return
 * `isIdentical: false` with a score at or above the threshold, and a row that
 * renders with the meter filled past the tick and no label reads as "this
 * passed" on a row that did not.
 */
export function bandLabel(conf: number, threshold: number): string {
  if (conf >= threshold) return 'Above threshold — Azure rejected the match';
  if (conf < 0.4) return 'Well below threshold';
  if (conf < threshold - 0.05) return 'Below threshold';
  return 'Just below threshold';
}

export function confPct(conf: number): number {
  return Math.round(conf * 100);
}

/** Red under 50%, amber at or above (handoff §6e). */
export function confTone(conf: number): 'low' | 'mid' {
  return conf < 0.5 ? 'low' : 'mid';
}

export function initials(firstName: string, lastName: string): string {
  return `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}`.toUpperCase() || '?';
}

/** Fixed 8-colour palette keyed on employeeID, so a person keeps one colour. */
const AVATAR_PALETTE = [
  '#2f6fed',
  '#1f9d6b',
  '#b1499c',
  '#d4881a',
  '#0e7c86',
  '#7a5cf0',
  '#e0533f',
  '#1f9488',
];
export function avatarColor(employeeID: number): string {
  return AVATAR_PALETTE[Math.abs(employeeID) % AVATAR_PALETTE.length];
}

/** The four fields the shared Enrollment Photo modal needs, from any employee-shaped record. */
function pickEmployee(e: {
  employeeID: number;
  altEmployeeID: string;
  firstName: string;
  lastName: string;
}): EnrollmentModalEmployee {
  return { employeeID: e.employeeID, altEmployeeID: e.altEmployeeID, firstName: e.firstName, lastName: e.lastName };
}

export function isPending(row: SelfieVerificationExceptionItem): boolean {
  return row.exceptionStatus === 'pending';
}

export function employeeName(row: SelfieVerificationExceptionItem): string {
  return `${row.employee.firstName} ${row.employee.lastName}`.trim();
}

/* ═══════════════════════════════════════════════════════════════════════════
   Float menu — a local fork of components/v2/FloatMenuButton.
   Forked rather than extended (the GpsExceptionsPage precedent): this page needs
   multi-select rows that don't close the menu, a footer action, a mono id label
   and a square colour swatch, which together roughly double the shared
   component's prop surface. Forking keeps the four existing callers of the
   shared one at zero regression risk.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface FloatMenuOption {
  value: string;
  label: string;
  /** Mono secondary label (employee number). Searched alongside `label`. */
  idLabel?: string | null;
  /** Square swatch — the reason colour. */
  dot?: string;
  count?: number;
}

/**
 * Touch devices don't get autoFocus on the filter box: React's autoFocus fires in the commit
 * phase, which on iOS Safari can land outside the user-gesture window that allows raising the
 * keyboard, so it appears and is immediately dismissed. Tapping the field always works.
 */
const isCoarsePointer = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;

const FM_GAP = 6;
const FM_EDGE = 12;
/** FM_LIST_MAX matches `.sfx-floatmenu .fm-list { max-height }` in the CSS. */
const FM_LIST_MAX = 320;
const FM_LIST_MIN = 120;

interface FloatMenuButtonProps {
  label: string;
  valueLabel: string;
  isPlaceholder: boolean;
  isActive: boolean;
  options: FloatMenuOption[];
  /** Single-select. */
  selectedValue?: string;
  onSelect?: (value: string) => void;
  /** Multi-select: checkbox rows that leave the menu open. */
  multiSelect?: boolean;
  selectedValues?: string[];
  onToggle?: (value: string) => void;
  footerAction?: { label: string; onClick: () => void };
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
}

function FloatMenuButton({
  label,
  valueLabel,
  isPlaceholder,
  isActive,
  options,
  selectedValue,
  onSelect,
  multiSelect,
  selectedValues,
  onToggle,
  footerAction,
  searchable,
  searchPlaceholder = 'Type to filter…',
  emptyLabel = 'No matches',
  disabled,
}: FloatMenuButtonProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; listMaxH: number } | null>(null);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHi(0);
    }
  }, [open]);
  useEffect(() => {
    setHi(0);
  }, [query]);

  const q = query.trim().toLowerCase();
  const visible =
    !searchable || !q
      ? options
      : options.filter((o) => `${o.label} ${o.idLabel ?? ''}`.toLowerCase().includes(q));

  const pick = (o: FloatMenuOption) => {
    if (multiSelect) {
      onToggle?.(o.value);
      return; // stays open — you are building a set
    }
    onSelect?.(o.value);
    setOpen(false);
  };

  const onSearchKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHi((h) => (visible.length ? (h + 1) % visible.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => (visible.length ? (h - 1 + visible.length) % visible.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (visible[hi]) pick(visible[hi]);
    }
  };

  // Keep the highlighted option visible by scrolling the list directly — never
  // scrollIntoView(), which can scroll the document instead and (on iOS) shift the
  // viewport out from under the open menu.
  useEffect(() => {
    if (!open) return;
    const list = menuRef.current?.querySelector('.fm-list') as HTMLElement | null;
    const el = list?.querySelector('.fm-opt.hl') as HTMLElement | null;
    if (!list || !el) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
  }, [hi, open]);

  // Fixed position measured off the trigger and clamped to the viewport; flips above when
  // there's more room there. Re-run on scroll/resize so the menu follows the trigger.
  const place = useCallback(() => {
    const t = triggerRef.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    const width = Math.max(r.width, 250);
    let left = r.left;
    if (left + width > window.innerWidth - FM_EDGE) left = Math.max(FM_EDGE, window.innerWidth - FM_EDGE - width);

    // visualViewport tracks the on-screen keyboard on iOS; window.innerHeight does not.
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const spaceBelow = vh - r.bottom - FM_GAP - FM_EDGE;
    const spaceAbove = r.top - FM_GAP - FM_EDGE;
    const below = spaceBelow >= spaceAbove;
    const avail = Math.max(below ? spaceBelow : spaceAbove, FM_LIST_MIN);

    // Non-list chrome = search header + footer + padding; measured once the menu exists.
    const menuEl = menuRef.current;
    const listEl = menuEl?.querySelector('.fm-list') as HTMLElement | null;
    const chrome = menuEl && listEl ? menuEl.offsetHeight - listEl.offsetHeight : 56;
    const listMaxH = Math.min(FM_LIST_MAX, Math.max(FM_LIST_MIN, Math.floor(avail) - chrome));
    const menuH = menuEl ? Math.min(menuEl.offsetHeight, chrome + listMaxH) : chrome + listMaxH;

    const top = below ? r.bottom + FM_GAP : Math.max(FM_EDGE, r.top - FM_GAP - menuH);

    setPos((p) => (p && p.top === top && p.left === left && p.width === width && p.listMaxH === listMaxH
      ? p
      : { top, left, width, listMaxH }));
  }, []);

  // visible.length: filtering changes the menu's height, and a menu flipped above the trigger
  // is anchored by its bottom edge — without this it drifts away as the list shrinks.
  useLayoutEffect(() => { if (open) place(); }, [open, place, visible.length]);

  // The menu only renders once `pos` exists, so the first place() has no menu to measure.
  // Re-place as the node attaches, now that its real height is known.
  const setMenuNode = useCallback((node: HTMLDivElement | null) => {
    menuRef.current = node;
    if (node) place();
  }, [place]);

  // Scroll/resize re-anchor the menu. They used to close it, which on iOS Safari was
  // self-cancelling: focusing the filter box raises the keyboard, Safari scrolls to reveal
  // the field, and that scroll unmounted the field it was scrolling to.
  useEffect(() => {
    if (!open) return;
    // pointerdown, not mousedown: iOS only synthesises mouse events for elements it treats
    // as tappable, so a tap on plain background wouldn't close the menu.
    const onDocDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    const vv = window.visualViewport;
    document.addEventListener('pointerdown', onDocDown);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    vv?.addEventListener('resize', place);
    vv?.addEventListener('scroll', place);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDocDown);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      vv?.removeEventListener('resize', place);
      vv?.removeEventListener('scroll', place);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);

  const isSelected = (o: FloatMenuOption) =>
    multiSelect ? !!selectedValues?.includes(o.value) : o.value === selectedValue;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={`fsel${isActive ? ' active' : ''}`}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="fs-lab">{label}</span>
        <span className={`fs-val${isPlaceholder ? ' placeholder' : ''}`}>{valueLabel}</span>
        <ChevronDown className="caret" />
      </button>
      {open &&
        pos &&
        ReactDOM.createPortal(
          <div
            ref={setMenuNode}
            className="sfx-floatmenu"
            style={{ top: pos.top, left: pos.left, width: pos.width }}
          >
            {searchable && (
              <div className="fm-search">
                <Search />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={query}
                  autoFocus={!isCoarsePointer()}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  enterKeyHint="done"
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onSearchKey}
                />
              </div>
            )}
            <div className="fm-list" style={{ maxHeight: pos.listMaxH }}>
              {visible.length === 0 ? (
                <div className="fm-empty">{emptyLabel}</div>
              ) : (
                visible.map((o, i) => (
                  <button
                    key={o.value}
                    type="button"
                    className={`fm-opt${isSelected(o) ? ' sel' : ''}${
                      searchable && i === hi ? ' hl' : ''
                    }`}
                    onClick={() => pick(o)}
                    onMouseEnter={searchable ? () => setHi(i) : undefined}
                  >
                    {multiSelect && (
                      <span className="box">
                        <Check />
                      </span>
                    )}
                    {o.dot && <span className="dot" style={{ background: o.dot }} />}
                    <span className="fm-lab">{o.label}</span>
                    {o.idLabel && <span className="fm-id">{o.idLabel}</span>}
                    {o.count != null ? (
                      <span className="fm-cnt">{o.count}</span>
                    ) : (
                      !multiSelect && <Check className="fm-check" />
                    )}
                  </button>
                ))
              )}
            </div>
            {footerAction && (
              <div className="fm-foot">
                <button type="button" onClick={footerAction.onClick}>
                  {footerAction.label}
                </button>
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Leaf components
   ═══════════════════════════════════════════════════════════════════════════ */

export function Avatar({
  employeeID,
  firstName,
  lastName,
  className,
}: {
  employeeID: number;
  firstName: string;
  lastName: string;
  className?: string;
}) {
  return (
    <span className={`av${className ? ` ${className}` : ''}`} style={{ background: avatarColor(employeeID) }}>
      {initials(firstName, lastName)}
    </span>
  );
}

/**
 * One hook per image — useBlobImageUrl cannot be called in a loop.
 *
 * `index` staggers the request so a 40-row period doesn't fire 40 authenticated
 * fetches at once (handoff §6a). The hook no-ops on a null path until the latch
 * arms. Rows are keyed by exception id, so this mount-only effect runs once per
 * exception rather than re-firing on every sort or filter change.
 */
function SelfieThumb({
  url,
  index,
  alt,
  className = 'thumb',
}: {
  url: string;
  index: number;
  alt: string;
  className?: string;
}) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setArmed(true), Math.min(index, 40) * 60);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { blobUrl, loading, error } = useBlobImageUrl(armed ? `${url}?size=thumb` : null);

  const state = !armed || loading ? ' loading' : error || !blobUrl ? ' failed' : '';
  return (
    <span className={`${className}${state}`}>
      {blobUrl && !loading && !error && <img src={blobUrl} alt={alt} />}
      {!!error && <ImageOff />}
    </span>
  );
}

function ReasonChip({ reason }: { reason: string }) {
  const meta = reasonMeta(reason);
  const { Icon } = meta;
  return (
    <>
      <span className={`rchip ${meta.cls}`}>
        <Icon />
        {meta.label}
      </span>
      <span className="rfam">{meta.family}</span>
    </>
  );
}

function PunchTypeBadge({ punchType }: { punchType: 'clock_in' | 'clock_out' }) {
  return punchType === 'clock_in' ? (
    <span className="pbadge in">
      <LogIn />
      Clock in
    </span>
  ) : (
    <span className="pbadge out">
      <LogOut />
      Clock out
    </span>
  );
}

/**
 * Confidence. When nothing was scored this renders NO meter element at all —
 * a dash squeezed into an empty bar reads as a rendering bug, and this is
 * roughly a third of rows.
 */
function ConfidenceCell({ conf, threshold }: { conf: number | null; threshold: number }) {
  if (conf == null) {
    return (
      <div className="conf none">
        <div className="cv">—</div>
        <div className="cband">Not scored</div>
      </div>
    );
  }
  const tone = confTone(conf);
  return (
    <div className="conf">
      <div className={`cv ${tone}`}>{confPct(conf)}%</div>
      <div className="meter">
        <div className={`fill ${tone}`} style={{ width: `${Math.min(100, confPct(conf))}%` }} />
        <div className="thr" style={{ left: `${confPct(threshold)}%` }} />
      </div>
      <div className="cband">{bandLabel(conf, threshold)}</div>
    </div>
  );
}

function TimeEntryCell({ row }: { row: SelfieVerificationExceptionItem }) {
  if (row.timeEntryDeleted) {
    return (
      <span className="delflag">
        <Trash2 />
        Punch deleted
      </span>
    );
  }
  if (!row.timeEntry) return <span className="te">—</span>;
  return row.timeEntry.type === 'hourly' ? (
    <span className="te">
      <span className="tekind">Hourly</span> #{row.timeEntry.timeEntryHourlyID}
    </span>
  ) : (
    <span className="te">
      <span className="tekind">Job</span> #{row.timeEntry.timeEntryID}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Skeletons and states
   ═══════════════════════════════════════════════════════════════════════════ */

function KpiSkeleton() {
  return (
    <div className="kpis skel-kpis">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="kpi">
          <span className="sk w2" />
          <span className="sk w4" style={{ marginTop: 12, height: 24 }} />
        </div>
      ))}
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="skel-card">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="skel-row">
          <span className="sk thumb-sk" />
          <span className="sk av-sk" />
          <span className="sk w1" />
          <span className="sk w2" />
          <span className="sk w3" />
          <span className="sk w4" />
          <span className="sk w5" />
        </div>
      ))}
    </div>
  );
}

function StateCard({
  tone,
  icon,
  title,
  body,
  stats,
  actions,
}: {
  tone: 'good' | 'caught' | 'zero';
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
  stats?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className={`state ${tone}`}>
      <div className="st-ic">{icon}</div>
      <h3>{title}</h3>
      <p>{body}</p>
      {stats && <div className="st-stats">{stats}</div>}
      {actions && <div className="st-act">{actions}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Quick-review popup (the inline ✓ on a pending row)
   ═══════════════════════════════════════════════════════════════════════════ */

function QuickReviewPopup({
  row,
  index,
  onClose,
  onSubmit,
}: {
  row: SelfieVerificationExceptionItem;
  index: number;
  onClose: () => void;
  /** Rejects on failure so this popup can render its own error. */
  onSubmit: (id: number, note: string | null) => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    taRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(row.selfieVerificationExceptionID, note.trim() || null);
      onClose();
    } catch (e) {
      // Keep the typed note; surface the error and re-enable the button.
      setError(reviewSelfieExceptionErrorMessage(e));
      setSaving(false);
    }
  };

  const meta = reasonMeta(row.exceptionReason);

  return (
    <div className="sfx-pop-bg" onMouseDown={onClose}>
      <div className="sfx-pop" onMouseDown={(e) => e.stopPropagation()}>
        <div className="pop-head">
          <div className="pi">
            <CheckCircle2 />
          </div>
          <div>
            <h3>Mark reviewed</h3>
            <div className="pop-sub">
              <span>{employeeName(row)}</span>
              <span>·</span>
              <span>{row.employee.altEmployeeID}</span>
              <span>·</span>
              <span>{formatDayMono(row.punchedAt)}</span>
            </div>
          </div>
          <button type="button" className="x" onClick={onClose} aria-label="Close">
            <X />
          </button>
        </div>
        <div className="pop-body">
          {/* Recap strip — so you are not reviewing blind from the list. */}
          <div className="pop-peek">
            <SelfieThumb
              url={row.selfieImageUrl}
              index={index}
              alt={`Punch selfie for ${employeeName(row)}`}
            />
            <div className="pk-txt">
              <b>{meta.label}</b>
              {row.azureConfidence != null
                ? ` · ${confPct(row.azureConfidence)}% confidence`
                : ' · not scored'}
              <br />
              {formatDateTimeMono(row.punchedAt)} ·{' '}
              {row.punchType === 'clock_in' ? 'clock in' : 'clock out'}
            </div>
          </div>
          <label className="gr-lab" htmlFor="sfx-qp-note">
            <MessageSquare />
            Note
            <span className="cnt">{note.length} / 1000</span>
          </label>
          <textarea
            id="sfx-qp-note"
            ref={taRef}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={'e.g. "Verified with employee by phone — lighting."'}
            maxLength={1000}
            disabled={saving}
          />
          {error && (
            <div className="pop-err">
              <AlertTriangle />
              {`Couldn't save: ${error}`}
            </div>
          )}
        </div>
        <div className="pop-foot">
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn review-solid" onClick={submit} disabled={saving}>
            {saving ? <RefreshCw className="spin" /> : <Check />}
            {saving ? 'Saving…' : 'Mark reviewed'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Row
   ═══════════════════════════════════════════════════════════════════════════ */

interface ExceptionRowProps {
  row: SelfieVerificationExceptionItem;
  index: number;
  threshold: number;
  active: boolean;
  repeatOrdinal: number | null;
  isNewHire: boolean;
  saving: boolean;
  rowError: string | undefined;
  onOpen: (id: number) => void;
  onQuickReview: (id: number) => void;
  onRetry: (id: number) => void;
}

const ExceptionRow = React.memo(function ExceptionRow({
  row,
  index,
  threshold,
  active,
  repeatOrdinal,
  isNewHire,
  saving,
  rowError,
  onOpen,
  onQuickReview,
  onRetry,
}: ExceptionRowProps) {
  const pending = isPending(row);
  const id = row.selfieVerificationExceptionID;

  return (
    <tr
      className={`exrow${active ? ' active' : ''}${pending ? '' : ' reviewed-row'}`}
      onClick={() => onOpen(id)}
    >
      <td className="col-thumb">
        <SelfieThumb
          url={row.selfieImageUrl}
          index={index}
          alt={`Punch selfie for ${employeeName(row)}`}
        />
      </td>
      <td>
        <div className="emp-cell">
          <Avatar
            employeeID={row.employee.employeeID}
            firstName={row.employee.firstName}
            lastName={row.employee.lastName}
          />
          <div>
            <div className="en">
              {employeeName(row)}
              {repeatOrdinal != null && (
                <span className="repeat">
                  <Repeat />
                  {ordinalLabel(repeatOrdinal)} this week
                </span>
              )}
              {isNewHire && (
                <span className="newhire">
                  <Sparkle />
                  New hire
                </span>
              )}
            </div>
            <div className="eid">{row.employee.altEmployeeID}</div>
          </div>
        </div>
      </td>
      <td>
        <div className="dt-cell">
          <div className="d">{formatDayMono(row.punchedAt)}</div>
          <div className="t">{formatTimeMono(row.punchedAt)}</div>
        </div>
      </td>
      <td>
        <PunchTypeBadge punchType={row.punchType} />
      </td>
      <td>
        <ReasonChip reason={row.exceptionReason} />
      </td>
      <td className="col-conf">
        <ConfidenceCell conf={row.azureConfidence} threshold={threshold} />
      </td>
      <td className="col-te">
        <TimeEntryCell row={row} />
      </td>
      <td className="col-rev">
        <div className="rev-cell">
          {pending ? (
            <span className="rev-pill un">
              <span className="dot" />
              Pending
            </span>
          ) : (
            <span
              className="rev-pill ok"
              title={reviewedTooltip(row)}
            >
              <span className="dot" />
              Reviewed
              {row.reviewNote && <MessageSquare className="note-ic" />}
            </span>
          )}
          {pending && (
            <button
              type="button"
              className={`rev-toggle${saving ? ' saving' : ''}`}
              aria-label="Mark reviewed"
              onClick={(e) => {
                // Otherwise the row's own click would also open the detail.
                e.stopPropagation();
                onQuickReview(row.selfieVerificationExceptionID);
              }}
            >
              {saving ? <RefreshCw /> : <Check />}
            </button>
          )}
          {rowError && (
            <span className="rev-err">
              <AlertTriangle />
              {"Couldn't save"}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRetry(id);
                }}
              >
                Retry
              </button>
            </span>
          )}
        </div>
      </td>
    </tr>
  );
});

/** English ordinals only — the page is hardcoded EN like every shipped v2 page. */
export function ordinalLabel(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

function reviewedTooltip(row: SelfieVerificationExceptionItem): string {
  const who = row.reviewedBy ?? 'Unknown';
  const when = row.reviewedAt ? formatDateTimeMono(row.reviewedAt) : '—';
  return row.reviewNote ? `${who} · ${when}\n\n${row.reviewNote}` : `${who} · ${when}`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Page
   ═══════════════════════════════════════════════════════════════════════════ */

const SelfieExceptionsPage: React.FC = () => {
  const { payPeriods, payPeriodID, userRole, pendingDeepLink } = useSiteContext();
  const selectPayPeriod = useSelectPayPeriod();

  // The v1 shell never loads pay periods — usePayPeriods runs only from
  // WeekSelector and LayoutV2, so in v1 it happens only because Dashboard
  // mounts first. A deep link or a limited user skips that entirely, so the page
  // fetches them itself. Under v2 this duplicates LayoutV2's call, which is
  // harmless (same data, same writes).
  const { error: payPeriodsError } = usePayPeriods();

  // ── server
  const [envelope, setEnvelope] = useState<SelfieVerificationExceptionsResponse | null>(null);
  const [rawRows, setRawRows] = useState<SelfieVerificationExceptionItem[]>([]);
  // Starts false, unlike GpsExceptionsPage: combined with the early return below
  // it is what stops payPeriodID === 0 pinning the page on a skeleton forever.
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);

  // ── filters (reset whenever the period changes)
  const [status, setStatus] = useState<StatusFilter>('pending');
  const [reasons, setReasons] = useState<string[]>([]);
  const [employeeID, setEmployeeID] = useState<number | ''>('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('punch_desc');

  // ── interaction
  const [openId, setOpenId] = useState<number | null>(null);
  const [quickReviewId, setQuickReviewId] = useState<number | null>(null);
  const [savingIds, setSavingIds] = useState<number[]>([]);
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [toast, setToast] = useState<{ msg: React.ReactNode; err: boolean; spin?: boolean } | null>(null);

  // ── Enrollment view (TAS-568): current state, not tied to the week. Local state,
  // default Exceptions; the `photo=` deep link targets the roster, not this page.
  const [view, setView] = useState<'exc' | 'enr'>('exc');
  const [issues, setIssues] = useState<EnrollmentIssue[] | null>(null);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesError, setIssuesError] = useState<string | null>(null);
  const [issuesRefreshedAt, setIssuesRefreshedAt] = useState<Date | null>(null);
  /** employeeID → sort time: a row retried/fixed in place keeps its slot until the next Refresh. */
  const [enrPins, setEnrPins] = useState<Record<number, string>>({});
  const [enrFlash, setEnrFlash] = useState<number | null>(null);
  const [enrRetrying, setEnrRetrying] = useState<number[]>([]);
  /** Enrollment changes made on this page (modal / inline Retry), newest truth for the detail. */
  const [enrOverrides, setEnrOverrides] = useState<Record<number, EmployeeEnrollment>>({});
  // The shared Enrollment Photo modal (Fix / View / Fix enrollment / Open other).
  const [enrollmentTarget, setEnrollmentTarget] = useState<{
    employee: EnrollmentModalEmployee;
    enrollment?: EmployeeEnrollment | null;
    /** Opened from a conflict's "Open {other}" — changes the delete toast. */
    viaOther?: boolean;
  } | null>(null);
  const issuesRef = useRef<EnrollmentIssue[] | null>(null);
  issuesRef.current = issues;
  const flashTimer = useRef<number | null>(null);

  const deepLinkConsumed = useRef(false);
  const periodAutoPicked = useRef(false);
  const toastTimer = useRef<number | null>(null);

  const selectedIdx = payPeriods.findIndex((pp) => pp.PayPeriodID === payPeriodID);

  const showToast = useCallback((msg: React.ReactNode, err = false, spin = false) => {
    setToast({ msg, err, spin });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), err ? 5000 : spin ? 4200 : 3400);
  }, []);

  useEffect(
    () => () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    },
    []
  );

  /* ── enrollment issues ───────────────────────────────────────────────── */

  const loadIssues = useCallback(
    async (manual: boolean) => {
      const prev = issuesRef.current;
      setIssuesLoading(true);
      setIssuesError(null);
      try {
        const data = await getEnrollmentIssues();
        setIssues(data.issues);
        setEnrPins({});
        setEnrOverrides({});
        setIssuesRefreshedAt(new Date());
        if (manual && prev) {
          // Rows that are no longer failed/verifying leave the list — name them.
          const open = (i: EnrollmentIssue) =>
            i.enrollment.state === 'failed' || i.enrollment.state === 'verifying';
          const still = new Set(data.issues.filter(open).map((i) => i.employee.employeeID));
          const left = prev.filter((i) => open(i) && !still.has(i.employee.employeeID));
          const nm = (i: EnrollmentIssue) => `${i.employee.firstName} ${i.employee.lastName}`;
          if (left.length === 1) {
            showToast(
              <>
                <b>{nm(left[0])}</b> is no longer failed or verifying and has left the list.
              </>
            );
          } else if (left.length > 1) {
            showToast(
              <>
                <b>{left.length}</b> employees left the list: {left.map(nm).join(', ')}.
              </>
            );
          }
        }
      } catch (e) {
        setIssuesError(getEnrollmentPhotoErrorMessage(e));
      } finally {
        setIssuesLoading(false);
      }
    },
    [showToast]
  );

  // Fetched on mount (not on tab switch) so the tab row can show the failed count.
  useEffect(() => {
    loadIssues(false);
  }, [loadIssues]);

  const flashRow = useCallback((employeeID: number) => {
    setEnrFlash(employeeID);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setEnrFlash(null), 1300);
  }, []);

  /** Patch one employee's enrollment everywhere on the page: the issues list, and the detail's fork. */
  const patchEnrollment = useCallback(
    (employeeID: number, next: EmployeeEnrollment, pin: boolean) => {
      setEnrOverrides((p) => ({ ...p, [employeeID]: next }));
      const before = issuesRef.current?.find((i) => i.employee.employeeID === employeeID);
      if (!before) return;
      if (pin && before.enrollment.state === 'failed') {
        const at =
          before.enrollment.failedAt ?? before.enrollment.queuedAt ?? before.enrollment.enrolledAt ?? new Date().toISOString();
        setEnrPins((p) => (employeeID in p ? p : { ...p, [employeeID]: at }));
      }
      setIssues((prev) =>
        prev ? prev.map((i) => (i.employee.employeeID === employeeID ? { ...i, enrollment: next } : i)) : prev
      );
      flashRow(employeeID);
    },
    [flashRow]
  );

  /** Inline Retry: direct, no confirm (idempotent; 409 when something is already in flight). */
  const retryFromList = useCallback(
    async (issue: EnrollmentIssue) => {
      const id = issue.employee.employeeID;
      const name = `${issue.employee.firstName} ${issue.employee.lastName}`;
      setEnrRetrying((p) => [...p, id]);
      try {
        const [resp] = await Promise.all([
          retryEnrollment(id),
          new Promise((r) => window.setTimeout(r, 500)),
        ]);
        patchEnrollment(id, resp.enrollment, true);
        showToast(
          <>
            Retry queued for <b>{name}</b>. Verifying, usually within 15 minutes.
          </>,
          false,
          true
        );
      } catch (e) {
        const conflict = getEnrollmentRetryConflict(e);
        if (conflict?.enrollment) patchEnrollment(id, conflict.enrollment, true);
        showToast(conflict?.message ?? `Couldn’t queue a retry for ${name}. ${getEnrollmentPhotoErrorMessage(e)}`, true);
      } finally {
        setEnrRetrying((p) => p.filter((x) => x !== id));
      }
    },
    [patchEnrollment, showToast]
  );

  const openFix = useCallback((issue: EnrollmentIssue) => {
    setEnrollmentTarget({ employee: pickEmployee(issue.employee), enrollment: issue.enrollment });
  }, []);

  const openOther = useCallback((m: EnrollmentMatchedEmployee) => {
    setEnrollmentTarget({ employee: pickEmployee(m), enrollment: null, viaOther: true });
  }, []);

  const onEnrollmentChanged = useCallback(
    (next: EmployeeEnrollment, action: EnrollmentChangeAction) => {
      const target = enrollmentTarget;
      if (!target) return;
      const name = <b>{`${target.employee.firstName} ${target.employee.lastName}`}</b>;
      patchEnrollment(target.employee.employeeID, next, action !== 'delete');
      setEnrollmentTarget((cur) => (cur ? { ...cur, enrollment: next } : cur));
      if (action === 'delete') {
        showToast(
          target.viaOther ? (
            <>
              Enrollment photo deleted for {name}. Retry the conflicting upload once the delete finishes (within 15
              minutes).
            </>
          ) : (
            <>Enrollment photo deleted for {name}.</>
          )
        );
      } else {
        showToast(
          <>
            {action === 'retry' ? 'Retry queued for' : 'New photo uploaded for'} {name}. Verifying, usually within 15
            minutes.
          </>,
          false,
          true
        );
      }
    },
    [enrollmentTarget, patchEnrollment, showToast]
  );

  const enrollmentCounts = useMemo(() => countEnrollmentIssues(issues), [issues]);
  const canEditEnrollment = userRole.admin || !!userRole.editEmployeeEnrollmentPhotos;

  /* ── load ────────────────────────────────────────────────────────────── */

  const load = useCallback(async () => {
    if (!payPeriodID) {
      // Clear the flag before returning, or the skeleton never resolves.
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getSelfieVerificationExceptions(payPeriodID);
      setEnvelope(data);
      setRawRows(data.exceptions);
      setRefreshedAt(new Date());
    } catch (e) {
      setLoadError(getSelfieVerificationExceptionsErrorMessage(e));
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }, [payPeriodID]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payPeriodID]);

  // Filters are period-scoped; stale selections would silently render an empty table.
  useEffect(() => {
    setStatus('pending');
    setReasons([]);
    setEmployeeID('');
    setQuery('');
    setSort('punch_desc');
    setOpenId(null);
    setQuickReviewId(null);
    setRowErrors({});
  }, [payPeriodID]);

  // usePayPeriods only auto-picks when week/year are both untouched, so a
  // carried-over week with payPeriodID 0 would leave nothing selected.
  useEffect(() => {
    if (periodAutoPicked.current) return;
    if (payPeriodID || payPeriods.length === 0) return;
    periodAutoPicked.current = true;
    const now = Date.now();
    const containing = payPeriods.find(
      (pp) => new Date(pp.StartDate).getTime() <= now && now <= new Date(pp.EndDate).getTime()
    );
    selectPayPeriod(containing ?? payPeriods[payPeriods.length - 1]);
  }, [payPeriods, payPeriodID, selectPayPeriod]);

  /* ── deep link ───────────────────────────────────────────────────────── */

  // useAdminSession clears pendingDeepLink in the same effect that sets the
  // page, and React batches those — so the context value may already be null by
  // the time this page mounts. The module-level stash covers that case.
  useEffect(() => {
    if (deepLinkConsumed.current) return;
    const link = pendingDeepLink?.exceptionId ? pendingDeepLink : consumeStashedDeepLink();
    if (!link?.exceptionId) return;
    deepLinkConsumed.current = true;

    if (link.payPeriodID && link.payPeriodID !== payPeriodID) {
      const target = payPeriods.find((pp) => pp.PayPeriodID === link.payPeriodID);
      if (target) selectPayPeriod(target);
      else if (payPeriods.length === 0) {
        // Pay periods haven't loaded yet — let the next pass try again.
        deepLinkConsumed.current = false;
        return;
      }
    }
    // Widen the status filter, or a target that is already reviewed would be
    // filtered out of the list the modal walks.
    setStatus('all');
    setOpenId(link.exceptionId);
  }, [pendingDeepLink, payPeriods, payPeriodID, selectPayPeriod]);

  // The deep-linked id may not exist in the period we ended up on.
  useEffect(() => {
    if (!hasLoadedOnce || openId == null || loading) return;
    if (!rawRows.some((r) => r.selfieVerificationExceptionID === openId)) {
      setOpenId(null);
      showToast("That exception isn't in this pay period.", true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLoadedOnce, rawRows]);

  /* ── derived ─────────────────────────────────────────────────────────── */

  const threshold = envelope?.matchThreshold ?? 0.6;

  /** Period-wide counts, so the reason menu doesn't move as you filter. */
  const reasonCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    rawRows.forEach((r) => {
      counts[r.exceptionReason] = (counts[r.exceptionReason] ?? 0) + 1;
    });
    return counts;
  }, [rawRows]);

  const employeeOptions = useMemo(() => {
    const map = new Map<number, { name: string; altEmployeeID: string; count: number }>();
    rawRows.forEach((r) => {
      const existing = map.get(r.employee.employeeID);
      if (existing) existing.count += 1;
      else
        map.set(r.employee.employeeID, {
          name: employeeName(r),
          altEmployeeID: r.employee.altEmployeeID,
          count: 1,
        });
    });
    return Array.from(map.entries())
      .map(([id, v]) => ({ employeeID: id, ...v }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rawRows]);

  /**
   * Each exception's chronological position within its own employee's week, and
   * the employee's period total — the badge only appears from 2 upward.
   */
  const { repeatOrdinal, repeatTotals } = useMemo(() => {
    const byEmployee = new Map<number, SelfieVerificationExceptionItem[]>();
    rawRows.forEach((r) => {
      const list = byEmployee.get(r.employee.employeeID);
      if (list) list.push(r);
      else byEmployee.set(r.employee.employeeID, [r]);
    });
    const ordinal = new Map<number, number>();
    const totals = new Map<number, number>();
    byEmployee.forEach((list, empID) => {
      totals.set(empID, list.length);
      [...list]
        .sort((a, b) => new Date(a.punchedAt).getTime() - new Date(b.punchedAt).getTime())
        .forEach((r, i) => ordinal.set(r.selfieVerificationExceptionID, i + 1));
    });
    return { repeatOrdinal: ordinal, repeatTotals: totals };
  }, [rawRows]);

  /** Only shown at 3+, per handoff §4. */
  const calloutTarget = useMemo(() => {
    let best: { employeeID: number; name: string; count: number; allMismatch: boolean } | null = null;
    repeatTotals.forEach((count, empID) => {
      if (count < 3) return;
      if (best && best.count >= count) return;
      const rows = rawRows.filter((r) => r.employee.employeeID === empID);
      best = {
        employeeID: empID,
        name: employeeName(rows[0]),
        count,
        allMismatch: rows.every((r) => r.exceptionReason === 'face_mismatch'),
      };
    });
    return best as { employeeID: number; name: string; count: number; allMismatch: boolean } | null;
  }, [repeatTotals, rawRows]);

  /** Hired inside the loaded period. Null hireDate means no badge, not a guess. */
  const newHireIDs = useMemo(() => {
    const ids = new Set<number>();
    if (!envelope) return ids;
    const start = parseDateOnly(envelope.startDate);
    const end = parseDateOnly(envelope.endDate);
    if (!start || !end) return ids;
    rawRows.forEach((r) => {
      const hired = parseDateOnly(r.employee.hireDate);
      if (hired && hired >= start && hired <= end) ids.add(r.employee.employeeID);
    });
    return ids;
  }, [rawRows, envelope]);

  const employeesAffected = useMemo(
    () => new Set(rawRows.map((r) => r.employee.employeeID)).size,
    [rawRows]
  );

  /**
   * KPI counts come from rawRows, not the envelope, so they stay correct after
   * an in-place review patch without a refetch. rawRows is period-wide and never
   * filtered, so "tiles never react to filters" still holds.
   */
  const pendingCount = useMemo(() => rawRows.filter(isPending).length, [rawRows]);
  const totalCount = rawRows.length;
  const reviewedCount = totalCount - pendingCount;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rawRows.filter((r) => {
      if (status === 'pending' && !isPending(r)) return false;
      if (status === 'reviewed' && isPending(r)) return false;
      if (reasons.length && !reasons.includes(r.exceptionReason)) return false;
      if (employeeID !== '' && r.employee.employeeID !== employeeID) return false;
      if (q) {
        const hay = `${r.employee.firstName} ${r.employee.lastName} ${r.employee.altEmployeeID}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rawRows, status, reasons, employeeID, query]);

  const sorted = useMemo(() => {
    const rows = [...filtered];
    const punchDesc = (a: SelfieVerificationExceptionItem, b: SelfieVerificationExceptionItem) =>
      new Date(b.punchedAt).getTime() - new Date(a.punchedAt).getTime();

    switch (sort) {
      case 'punch_asc':
        rows.sort((a, b) => new Date(a.punchedAt).getTime() - new Date(b.punchedAt).getTime());
        break;
      case 'conf_asc':
        // Unscored rows sort LAST — they are not "the worst match", they are no
        // match at all. Ties fall back to newest punch.
        rows.sort((a, b) => {
          const av = a.azureConfidence;
          const bv = b.azureConfidence;
          if (av == null && bv == null) return punchDesc(a, b);
          if (av == null) return 1;
          if (bv == null) return -1;
          return av === bv ? punchDesc(a, b) : av - bv;
        });
        break;
      case 'employee':
        rows.sort((a, b) => {
          const cmp = employeeName(a).localeCompare(employeeName(b));
          return cmp !== 0 ? cmp : punchDesc(a, b);
        });
        break;
      default:
        rows.sort(punchDesc);
    }
    return rows;
  }, [filtered, sort]);

  // Looked up by id every render, never captured — so an open modal reflects the
  // in-place review patch instead of a stale copy.
  const openRow = useMemo(
    () => (openId == null ? null : rawRows.find((r) => r.selfieVerificationExceptionID === openId) ?? null),
    [rawRows, openId]
  );
  const openIndex = useMemo(
    () => (openId == null ? -1 : sorted.findIndex((r) => r.selfieVerificationExceptionID === openId)),
    [sorted, openId]
  );
  const quickReviewRow = useMemo(
    () =>
      quickReviewId == null
        ? null
        : rawRows.find((r) => r.selfieVerificationExceptionID === quickReviewId) ?? null,
    [rawRows, quickReviewId]
  );

  const filtersActive =
    status !== 'pending' || reasons.length > 0 || employeeID !== '' || query.trim() !== '';

  /* ── review ──────────────────────────────────────────────────────────── */

  const applyReviewResult = useCallback(
    (
      id: number,
      review: { reviewedBy: string | null; reviewedAt: string | null; reviewNote: string | null }
    ) => {
      setRawRows((prev) =>
        prev.map((r) =>
          r.selfieVerificationExceptionID === id
            ? { ...r, exceptionStatus: 'reviewed', ...review }
            : r
        )
      );
    },
    []
  );

  /** Rejects on failure — the popup and the modal render their own errors. */
  const submitReview = useCallback(
    async (id: number, note: string | null) => {
      setSavingIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setRowErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      try {
        const result = await reviewSelfieException(id, note);
        applyReviewResult(id, {
          reviewedBy: result.reviewedBy,
          reviewedAt: result.reviewedAt,
          reviewNote: result.reviewNote,
        });
        const row = rawRows.find((r) => r.selfieVerificationExceptionID === id);
        showToast(`Marked reviewed${row ? ` · ${employeeName(row)}` : ''}`);
      } catch (e) {
        const conflict = getReviewConflict(e);
        if (conflict) {
          // Somebody else got there first. The row really is reviewed — show the
          // truth rather than leaving it pending behind a red toast.
          applyReviewResult(id, {
            reviewedBy: conflict.reviewedBy,
            reviewedAt: conflict.reviewedAt,
            reviewNote: conflict.reviewNote,
          });
          showToast(
            conflict.reviewedBy
              ? `Already reviewed by ${conflict.reviewedBy}.`
              : 'This exception was already reviewed.',
            true
          );
          return; // resolves: the popup should close, not show an error
        }
        showToast('Couldn’t save the review — check your connection and try again.', true);
        setRowErrors((prev) => ({ ...prev, [id]: reviewSelfieExceptionErrorMessage(e) }));
        throw e;
      } finally {
        setSavingIds((prev) => prev.filter((x) => x !== id));
      }
    },
    [applyReviewResult, rawRows, showToast]
  );

  /**
   * Reviewing from the detail while filtered to Pending advances to the next
   * pending item rather than closing — that is what makes this a queue.
   */
  const reviewFromDetail = useCallback(
    async (id: number, note: string | null) => {
      const nextPendingId =
        status === 'pending'
          ? sorted
              .slice(sorted.findIndex((r) => r.selfieVerificationExceptionID === id) + 1)
              .find((r) => isPending(r))?.selfieVerificationExceptionID ?? null
          : undefined;
      await submitReview(id, note);
      if (nextPendingId !== undefined) setOpenId(nextPendingId);
    },
    [status, sorted, submitReview]
  );

  const clearFilters = useCallback(() => {
    setStatus('pending');
    setReasons([]);
    setEmployeeID('');
    setQuery('');
  }, []);

  /** The employee's newest known enrollment: a change made here, else the issues list, else the row. */
  const effectiveEnrollment = useCallback(
    (emp: SelfieVerificationExceptionEmployee): EmployeeEnrollment | undefined =>
      enrOverrides[emp.employeeID] ??
      issues?.find((i) => i.employee.employeeID === emp.employeeID)?.enrollment ??
      emp.enrollment,
    [enrOverrides, issues]
  );

  const openEnrollment = useCallback(
    (emp: SelfieVerificationExceptionEmployee) => {
      setEnrollmentTarget({ employee: pickEmployee(emp), enrollment: effectiveEnrollment(emp) ?? null });
    },
    [effectiveEnrollment]
  );

  /* ── render helpers ──────────────────────────────────────────────────── */

  const showSkeleton = (loading && !hasLoadedOnce) || (payPeriods.length === 0 && !payPeriodsError);
  const showRefreshing = loading && hasLoadedOnce;
  const noPeriodSelected = payPeriods.length > 0 && selectedIdx < 0;

  const reasonValueLabel =
    reasons.length === 0
      ? 'All reasons'
      : reasons.length === 1
      ? reasonMeta(reasons[0]).label
      : `${reasons.length} reasons`;

  const employeeValueLabel =
    employeeID === ''
      ? 'All employees'
      : employeeOptions.find((o) => o.employeeID === employeeID)?.name ?? 'All employees';

  return (
    <div className={`sfx${config.version2 ? '' : ' sfx-shell1'}`}>
      <div className="sect">
        Timesheets <ChevronRight /> <b>Selfie Exceptions</b>
      </div>

      <div className="page-head">
        <div className="ph-ic">
          <ScanFace />
        </div>
        <div>
          <h1>Selfie Exceptions</h1>
          {view === 'enr' ? (
            <div className="ph-sub">
              Employees whose enrollment photo Azure rejected, or is still checking. Until it&rsquo;s
              fixed, every punch they make raises a <b>Not enrolled</b> exception. This is current
              state, so the week picker doesn&rsquo;t apply.
            </div>
          ) : (
            <div className="ph-sub">
              Punch selfies that failed to verify against the employee&rsquo;s enrollment photo.
              Compare the two photos, then mark each one reviewed — corrections to the punch itself
              still happen in Timesheet Review.
            </div>
          )}
        </div>
        <div className="ph-right">
          {view === 'exc' && payPeriods.length > 0 && (
            <div className="periodbox">
              <WeekPickerNav
                payPeriods={payPeriods}
                selectedIdx={selectedIdx}
                onChange={(idx) => {
                  const pp = payPeriods[idx];
                  if (pp) selectPayPeriod(pp);
                }}
              />
            </div>
          )}
          {view === 'exc' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {refreshedAt && (
                <span className="refreshed">
                  Refreshed{' '}
                  {refreshedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
              <button type="button" className="btn sm" onClick={load} disabled={loading || !payPeriodID}>
                <RefreshCw className={loading ? 'spin' : ''} />
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          ) : (
            // Refresh on the Enrollment view re-fetches only the issues list.
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {issuesRefreshedAt && (
                <span className="refreshed">
                  Refreshed{' '}
                  {issuesRefreshedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
              <button type="button" className="btn sm" onClick={() => loadIssues(true)} disabled={issuesLoading}>
                <RefreshCw className={issuesLoading ? 'spin' : ''} />
                {issuesLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* view switch (Q1): Exceptions {pending} · Enrollment {failed} ◌ {n} verifying */}
      <div className="vtabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'exc'}
          className={`vtab${view === 'exc' ? ' on' : ''}`}
          onClick={() => setView('exc')}
        >
          Exceptions
          <span className="vt-n">{hasLoadedOnce ? pendingCount : '–'}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'enr'}
          className={`vtab${view === 'enr' ? ' on' : ''}`}
          onClick={() => setView('enr')}
        >
          Enrollment
          {issues && (
            <span className={`vt-n ${enrollmentCounts.failed ? 'fail' : 'zero'}`}>{enrollmentCounts.failed}</span>
          )}
          {enrollmentCounts.verifying > 0 && (
            <span className="vt-v">
              <EnmSpin />
              {enrollmentCounts.verifying} verifying
            </span>
          )}
        </button>
        <span className="vt-scope">
          <Clock />
          {view === 'enr'
            ? 'Current state · not tied to a week'
            : envelope
            ? `Week of ${formatPeriodRange(envelope.startDate, envelope.endDate)}`
            : 'Week'}
        </span>
      </div>

      {view === 'enr' && (
        <div className="en-view">
          <SelfieEnrollmentView
            issues={issues}
            loading={issuesLoading}
            error={issuesError}
            canEdit={canEditEnrollment}
            pins={enrPins}
            flashId={enrFlash}
            retrying={enrRetrying}
            onReload={() => loadIssues(true)}
            onRetry={retryFromList}
            onFix={openFix}
            onOpenOther={openOther}
          />
        </div>
      )}

      {view === 'exc' && payPeriodsError && (
        <div className="errbanner">
          <AlertTriangle />
          <div>
            <b>Couldn&rsquo;t load pay periods.</b>
            <span className="em">{payPeriodsError}</span>
          </div>
          <button type="button" className="btn sm" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      )}

      {view === 'exc' && loadError && (
        <div className="errbanner">
          <AlertTriangle />
          <div>
            <b>Couldn&rsquo;t load this week&rsquo;s exceptions.</b>
            <span className="em">{loadError}</span>
          </div>
          <button type="button" className="btn sm" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {view === 'enr' ? null : showSkeleton ? (
        <>
          <KpiSkeleton />
          <SkeletonRows />
        </>
      ) : noPeriodSelected ? (
        <StateCard
          tone="zero"
          icon={<Inbox />}
          title="Pick a week"
          body="Choose a pay period above to see the selfie exceptions raised in it."
        />
      ) : (
        <>
          <div className="kpis">
            <div className="kpi total">
              <div className="kl">
                <ScanFace />
                Exceptions
              </div>
              <div className="kv">{totalCount}</div>
              <div className="ks">this week</div>
            </div>
            <div className="kpi pend">
              <div className="kl">
                <Inbox />
                Pending
              </div>
              <div className={`kv${pendingCount === 0 ? ' zero' : ''}`}>{pendingCount}</div>
              <div className="ks">still need a look</div>
            </div>
            <div className="kpi done">
              <div className="kl">
                <CheckCircle2 />
                Reviewed
              </div>
              <div className="kv">{reviewedCount}</div>
              <div className="ks">closed out</div>
            </div>
            <div className="kpi emp">
              <div className="kl">
                <Users />
                Employees
              </div>
              <div className="kv">{employeesAffected}</div>
              <div className="ks">
                {employeesAffected === 1 ? '1 of the crew flagged' : `${employeesAffected} of the crew flagged`}
              </div>
            </div>
          </div>

          {calloutTarget && (
            <div className="callout">
              <div className="co-ic">
                <Repeat />
              </div>
              <div>
                <b>{calloutTarget.name}</b> has {calloutTarget.count} exceptions this week
                {calloutTarget.allMismatch ? ', all face mismatches' : ''} — worth a look before the
                week is approved.
              </div>
              <button
                type="button"
                className="co-jump"
                onClick={() => {
                  setEmployeeID(calloutTarget.employeeID);
                  setStatus('all');
                }}
              >
                <ListFilter />
                Show just this employee
              </button>
            </div>
          )}

          <div className="filterbar">
            <div className="searchwrap">
              <Search />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search employee name or ID…"
              />
            </div>
            <div className="fb-controls">
              <FloatMenuButton
                label="Reason"
                valueLabel={reasonValueLabel}
                isPlaceholder={reasons.length === 0}
                isActive={reasons.length > 0}
                multiSelect
                selectedValues={reasons}
                onToggle={(value) =>
                  setReasons((prev) =>
                    prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
                  )
                }
                footerAction={{ label: 'Clear reasons', onClick: () => setReasons([]) }}
                options={REASON_ORDER.filter((r) => reasonCounts[r]).map((r) => ({
                  value: r,
                  label: REASON_META[r].label,
                  dot: REASON_META[r].dot,
                  count: reasonCounts[r],
                }))}
              />
              <FloatMenuButton
                label="Employee"
                valueLabel={employeeValueLabel}
                isPlaceholder={employeeID === ''}
                isActive={employeeID !== ''}
                searchable
                searchPlaceholder="Type to find…"
                emptyLabel="No employee matches"
                selectedValue={employeeID === '' ? '' : String(employeeID)}
                onSelect={(value) => setEmployeeID(value === '' ? '' : parseInt(value, 10))}
                options={[
                  { value: '', label: 'All employees' },
                  ...employeeOptions.map((o) => ({
                    value: String(o.employeeID),
                    label: o.name,
                    idLabel: o.altEmployeeID,
                    count: o.count,
                  })),
                ]}
              />
              <FloatMenuButton
                label="Sort"
                valueLabel={SORT_OPTIONS.find((o) => o.value === sort)?.label ?? ''}
                isPlaceholder={false}
                isActive={sort !== 'punch_desc'}
                selectedValue={sort}
                onSelect={(value) => setSort(value as SortKey)}
                options={SORT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              />

              <div className="segmented stat-seg">
                {(['pending', 'reviewed', 'all'] as StatusFilter[]).map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-stat={s}
                    className={status === s ? 'on' : ''}
                    onClick={() => setStatus(s)}
                  >
                    {s === 'pending' ? 'Pending' : s === 'reviewed' ? 'Reviewed' : 'All'}
                    {s === 'pending' && (
                      <span className="segcount" data-zero={pendingCount === 0 ? '1' : '0'}>
                        {pendingCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {filtersActive && (
                <button type="button" className="clear-btn" onClick={clearFilters}>
                  <XCircle />
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="resultline">
            <span>
              <b>{sorted.length}</b>{' '}
              {sorted.length === 1 ? 'exception' : 'exceptions'}
              {status === 'pending' ? ' pending' : status === 'reviewed' ? ' reviewed' : ''}
              {envelope ? ` · week of ${formatPeriodRange(envelope.startDate, envelope.endDate)}` : ''}
            </span>
            {filtersActive && <span className="filt-note">· filtered · KPIs stay week-wide</span>}
          </div>

          {totalCount === 0 && loadError ? null : totalCount === 0 ? (
            <StateCard
              tone="good"
              icon={<CheckCircle2 />}
              title="No selfie exceptions this week"
              body="Every punch selfie matched its enrollment photo. Nothing to review."
            />
          ) : pendingCount === 0 && !filtersActive ? (
            <StateCard
              tone="caught"
              icon={<CheckCircle2 />}
              title="Every exception this week is reviewed"
              body={
                <>
                  Nothing left in the pending queue. Switch to <b>Reviewed</b> or <b>All</b> to see
                  the notes you left.
                </>
              }
              stats={
                <>
                  <span>
                    <b>{totalCount}</b> exceptions
                  </span>
                  <span>
                    <b>{employeesAffected}</b> employees
                  </span>
                </>
              }
              actions={
                <button type="button" className="btn" onClick={() => setStatus('all')}>
                  Show all exceptions
                </button>
              }
            />
          ) : sorted.length === 0 ? (
            <StateCard
              tone="zero"
              icon={<Search />}
              title="No exceptions match these filters"
              body="Try clearing a filter or broadening your search — this week still has exceptions on record."
              actions={
                <button type="button" className="btn" onClick={clearFilters}>
                  Clear filters
                </button>
              }
            />
          ) : (
            <div className={`table-card${showRefreshing ? ' refreshing' : ''}`}>
              <table className="xt">
                <thead>
                  <tr>
                    <th className="col-thumb">Selfie</th>
                    <th>Employee</th>
                    <th>Punch</th>
                    <th>Type</th>
                    <th>Reason</th>
                    <th className="col-conf">Confidence</th>
                    <th className="col-te">Time entry</th>
                    <th className="col-rev">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row, i) => (
                    <ExceptionRow
                      // Keyed by id, not index: keeps each row's image hook and
                      // its one-shot stagger alive across sorts and filters.
                      key={row.selfieVerificationExceptionID}
                      row={row}
                      index={i}
                      threshold={threshold}
                      active={openId === row.selfieVerificationExceptionID}
                      repeatOrdinal={
                        (repeatTotals.get(row.employee.employeeID) ?? 0) >= 2
                          ? repeatOrdinal.get(row.selfieVerificationExceptionID) ?? null
                          : null
                      }
                      isNewHire={newHireIDs.has(row.employee.employeeID)}
                      saving={savingIds.includes(row.selfieVerificationExceptionID)}
                      rowError={rowErrors[row.selfieVerificationExceptionID]}
                      onOpen={setOpenId}
                      onQuickReview={setQuickReviewId}
                      onRetry={setQuickReviewId}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {openRow && (
        <SelfieExceptionDetailModal
          row={openRow}
          index={openIndex}
          total={sorted.length}
          threshold={threshold}
          repeatOrdinal={
            (repeatTotals.get(openRow.employee.employeeID) ?? 0) >= 2
              ? repeatOrdinal.get(openRow.selfieVerificationExceptionID) ?? null
              : null
          }
          repeatTotal={repeatTotals.get(openRow.employee.employeeID) ?? 0}
          saving={savingIds.includes(openRow.selfieVerificationExceptionID)}
          onClose={() => setOpenId(null)}
          onPrev={
            openIndex > 0
              ? () => setOpenId(sorted[openIndex - 1].selfieVerificationExceptionID)
              : undefined
          }
          onNext={
            openIndex >= 0 && openIndex < sorted.length - 1
              ? () => setOpenId(sorted[openIndex + 1].selfieVerificationExceptionID)
              : undefined
          }
          onSubmitReview={reviewFromDetail}
          enrollment={effectiveEnrollment(openRow.employee)}
          onOpenEnrollment={openEnrollment}
        />
      )}

      {quickReviewRow && isPending(quickReviewRow) && (
        <QuickReviewPopup
          row={quickReviewRow}
          index={0}
          onClose={() => setQuickReviewId(null)}
          onSubmit={submitReview}
        />
      )}

      {/* Shared Enrollment Photo modal (z-index 600 — stacks above the detail when opened from it) */}
      {enrollmentTarget && (
        <EnrollmentPhotoModal
          employee={enrollmentTarget.employee}
          enrollment={enrollmentTarget.enrollment}
          canEdit={canEditEnrollment}
          onClose={() => setEnrollmentTarget(null)}
          onChanged={onEnrollmentChanged}
          onOpenOther={openOther}
          onError={(message) => showToast(message, true)}
        />
      )}

      {toast && (
        <div className="sfx-toasts">
          <div className={`sfx-toast ${toast.err ? 'bad' : 'ok'}`}>
            {toast.spin ? <EnmSpin light /> : toast.err ? <AlertTriangle /> : <CheckCircle2 />}
            <span>{toast.msg}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelfieExceptionsPage;
